import React, { useState, useEffect, createContext, useContext } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Breadcrumbs from './Breadcrumbs';
import { NewLeadNotifier } from './NewLeadNotifier';
import { Toaster } from './ui/sonner';
import { useData } from '../contexts/DataContext';
import { toast } from 'sonner';
import { Menu, X, LogOut, User } from 'lucide-react';

// Context para el estado del sidebar
export const SidebarContext = createContext();

export const useSidebar = () => useContext(SidebarContext);

const MainLayout = ({ user, onLogout }) => {
  const { dispatch } = useData();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('datacrm_sidebar_collapsed');
    return saved === 'true';
  });
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate('/login');
    toast.success('Sesión cerrada');
  };

  // Guardar preferencia del sidebar
  useEffect(() => {
    localStorage.setItem('datacrm_sidebar_collapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Listener para capturar leads desde formularios externos (iframe/postMessage)
  useEffect(() => {
    const handleFormCapture = (event) => {
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          return;
        }
      }
      
      if (data && data.type === 'DATACRM_FORM_CAPTURE') {
        console.log('Form capture received:', data);
        const { pipelineId, formData } = data;
        
        let stageId = '';
        try {
          const pipelines = JSON.parse(localStorage.getItem('datacrm_pipelines') || '[]');
          const pipeline = pipelines.find(p => p.id === pipelineId);
          if (pipeline && pipeline.stages && pipeline.stages.length > 0) {
            stageId = pipeline.stages[0].id;
          }
        } catch (e) {
          console.error('Error obteniendo pipeline:', e);
        }
        
        const newLead = {
          id: `lead-${Date.now()}`,
          name: formData.name || formData.nombre || 'Sin nombre',
          email: formData.email || '',
          phone: formData.phone || formData.telefono || '',
          company: formData.company || formData.empresa || '',
          value: parseFloat(formData.budget || formData.presupuesto || formData.value || 0) || 0,
          pipelineId: pipelineId,
          stageId: stageId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          data: formData,
          priority: 'Media',
          source: 'form_capture'
        };

        dispatch({ type: 'ADD_LEAD', payload: newLead });
        toast.success(`¡Nuevo lead capturado! ${newLead.name}`);
      }
    };

    window.addEventListener('message', handleFormCapture);
    return () => window.removeEventListener('message', handleFormCapture);
  }, [dispatch]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <SidebarContext.Provider value={{ sidebarCollapsed, setSidebarCollapsed, toggleSidebar }}>
      <div className="flex h-screen w-full bg-slate-50 overflow-hidden">
        {/* Sidebar */}
        <Sidebar collapsed={sidebarCollapsed} />
        
        {/* Main Content */}
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Toggle Button */}
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-600"
                title={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
              >
                {sidebarCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
              </button>
              
              {/* Breadcrumbs */}
              <Breadcrumbs />
            </div>
            
            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="text-left hidden sm:block">
                  <p className="text-sm font-medium text-slate-900">{user?.name || 'Usuario'}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role || 'admin'}</p>
                </div>
              </button>
              
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Page Content */}
          <div className="flex-1 overflow-y-auto">
            <Outlet />
          </div>
        </main>
        
        <NewLeadNotifier user={user} />
        <Toaster position="top-right" richColors />
      </div>
    </SidebarContext.Provider>
  );
};

export default MainLayout;
